import React, { useState, useEffect } from 'react';
import { Users, Phone, CheckCircle, Calendar, RefreshCw, PieChart, X, User, Globe, Check, ChevronDown, ChevronUp, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_BASE_URL } from '../config';

const JKStatistics = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Inizializza con il mese corrente
  const getCurrentMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return {
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0]
    };
  };

  const [dateRange, setDateRange] = useState(getCurrentMonthRange());
  
  // Stati per filtro utente
  const [jkUsers, setJkUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showGlobalStats, setShowGlobalStats] = useState(true);
  
  // Stati per la paginazione della tabella delle chiamate
  const [currentPage, setCurrentPage] = useState(1);
  const [callsPerPage] = useState(10);
  
  // Stato per il filtro chiamate sospette
  const [showSuspiciousCalls, setShowSuspiciousCalls] = useState(false);

  // Funzione per ottenere il token di autenticazione
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Funzione per caricare gli utenti JK
  const fetchJKUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      });

      if (response.ok) {
        const users = await response.json();
        // Filtra solo utenti JK (inclusi JK Manager)
        const jkOnlyUsers = users.filter(user => 
          user.department === 'JK' && ['user', 'agent', 'jk_manager'].includes(user.role)
        );
        setJkUsers(jkOnlyUsers);
      }
    } catch (error) {
      console.error('Errore nel caricamento utenti JK:', error);
    }
  };

  // Funzione per ottenere il nome dell'utente selezionato
  const getSelectedUserName = () => {
    if (showGlobalStats) {
      return 'Alle Benutzer';
    }
    const user = jkUsers.find(u => u.id === selectedUserId);
    return user ? user.username : 'Benutzer auswählen';
  };

  // Funzione per caricare le statistiche
  const loadStatistics = async () => {
    setLoading(true);
    setError(null);

    try {
      let url = `${API_BASE_URL}/jk-statistics`;
      const params = new URLSearchParams();
      
      if (dateRange.startDate) params.append('start_date', dateRange.startDate);
      if (dateRange.endDate) params.append('end_date', dateRange.endDate);
      if (!showGlobalStats && selectedUserId) params.append('user_id', selectedUserId);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setStatistics(data);
    } catch (error) {
      console.error('Errore nel caricamento delle statistiche:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Carica le statistiche al mount del componente
  useEffect(() => {
    fetchJKUsers();
    loadStatistics();
  }, []);

  // Ricarica statistiche quando cambiano i filtri
  useEffect(() => {
    loadStatistics();
  }, [showGlobalStats, selectedUserId]);

  // Gestione cambio date
  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Applica filtro date
  const applyDateFilter = () => {
    loadStatistics();
  };

  // Reset filtri
  const resetFilters = () => {
    setDateRange(getCurrentMonthRange());
    setShowGlobalStats(true);
    setSelectedUserId(null);
    setShowUserDropdown(false);
    setCurrentPage(1); // Reset paginazione
    setTimeout(() => loadStatistics(), 100);
  };

  // Funzione per identificare chiamate sospette
  const isSuspiciousCall = (call) => {
    const hasPositiveOrNegativeFeedback = 
      call.new_feedback === 'positive' || 
      call.new_feedback === 'CustomerFeedback.positive' || 
      (typeof call.new_feedback === 'object' && call.new_feedback?.name === 'positive') ||
      call.new_feedback === 'negative' || 
      call.new_feedback === 'CustomerFeedback.negative' || 
      (typeof call.new_feedback === 'object' && call.new_feedback?.name === 'negative');
    
    const hasShortDuration = call.call_duration && call.call_duration < 15;
    
    return hasPositiveOrNegativeFeedback && hasShortDuration;
  };

  // Filtra le chiamate in base al toggle
  const getFilteredCalls = () => {
    const allCalls = statistics?.calls || [];
    if (showSuspiciousCalls) {
      return allCalls.filter(isSuspiciousCall);
    }
    return allCalls;
  };

  const filteredCalls = getFilteredCalls();
  const suspiciousCallsCount = (statistics?.calls || []).filter(isSuspiciousCall).length;

  // Funzioni per la paginazione
  const indexOfLastCall = currentPage * callsPerPage;
  const indexOfFirstCall = indexOfLastCall - callsPerPage;
  const currentCalls = filteredCalls.slice(indexOfFirstCall, indexOfLastCall);
  const totalPages = Math.ceil(filteredCalls.length / callsPerPage);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Funzione per toggle del filtro chiamate sospette
  const toggleSuspiciousCallsFilter = () => {
    setShowSuspiciousCalls(!showSuspiciousCalls);
    setCurrentPage(1); // Reset paginazione quando si cambia filtro
  };

  // Funzione per formattare la data
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  // Funzione per formattare la durata della chiamata
  const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '-';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  };

  // Funzioni per formattare status e feedback come nelle statistiche legacy
  const getStatusLabel = (status) => {
    if (status === 'sold' || 
        status === 'CustomerStatus.sold' || 
        (typeof status === 'object' && status?.name === 'sold')) {
      return 'Verkauft';
    } else if (status === 'closed' || 
               status === 'CustomerStatus.closed' || 
               (typeof status === 'object' && status?.name === 'closed')) {
      return 'Geschlossen';
    } else if (status === 'not_available' || 
               status === 'CustomerStatus.not_available' || 
               (typeof status === 'object' && status?.name === 'not_available')) {
      return 'Nicht erreicht';
    } else if (status === 'open' || 
               status === 'CustomerStatus.open' || 
               (typeof status === 'object' && status?.name === 'open')) {
      return 'Offen';
    } else {
      return status || '-';
    }
  };

  const getStatusColor = (status) => {
    if (status === 'sold' || 
        status === 'CustomerStatus.sold' || 
        (typeof status === 'object' && status?.name === 'sold')) {
      return 'bg-green-100 text-green-800';
    } else if (status === 'closed' || 
               status === 'CustomerStatus.closed' || 
               (typeof status === 'object' && status?.name === 'closed')) {
      return 'bg-red-100 text-red-800';
    } else if (status === 'not_available' || 
               status === 'CustomerStatus.not_available' || 
               (typeof status === 'object' && status?.name === 'not_available')) {
      return 'bg-yellow-100 text-yellow-800';
    } else {
      return 'bg-blue-100 text-blue-800';
    }
  };

  const getFeedbackLabel = (feedback) => {
    if (feedback === 'positive' || 
        feedback === 'CustomerFeedback.positive' || 
        (typeof feedback === 'object' && feedback?.name === 'positive')) {
      return 'Positiv';
    } else if (feedback === 'negative' || 
               feedback === 'CustomerFeedback.negative' || 
               (typeof feedback === 'object' && feedback?.name === 'negative')) {
      return 'Negativ';
    } else if (feedback === 'neutral' || 
               feedback === 'CustomerFeedback.neutral' || 
               (typeof feedback === 'object' && feedback?.name === 'neutral')) {
      return 'Nicht erreicht';
    } else {
      return feedback || '-';
    }
  };

  const getFeedbackColor = (feedback) => {
    if (feedback === 'positive' || 
        feedback === 'CustomerFeedback.positive' || 
        (typeof feedback === 'object' && feedback?.name === 'positive')) {
      return 'bg-green-100 text-green-800';
    } else if (feedback === 'negative' || 
               feedback === 'CustomerFeedback.negative' || 
               (typeof feedback === 'object' && feedback?.name === 'negative')) {
      return 'bg-red-100 text-red-800';
    } else if (feedback === 'neutral' || 
               feedback === 'CustomerFeedback.neutral' || 
               (typeof feedback === 'object' && feedback?.name === 'neutral')) {
      return 'bg-yellow-100 text-yellow-800';
    } else {
      return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">Lade Statistiken...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <div className="text-red-600">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Fehler beim Laden der Statistiken</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={loadStatistics}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold flex items-center">
          <PieChart className="mr-2 h-5 w-5 text-indigo-500" />
          Statistiken
        </h2>
      </div>
      <div className="p-6">
              <div className="bg-white shadow-sm rounded-lg p-4 sm:p-6">
                {/* Sezione dei filtri */}
                <div className="mb-4">
                  {/* Filtri in una riga */}
                  <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between mb-3">
                    {/* Filtri date e utente */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                      {/* Filtro date */}
                      <div className="flex flex-wrap items-center gap-2">
                        <Calendar className="h-5 w-5 text-gray-500 flex-shrink-0" />
                        <div className="flex items-center">
                          <input
                            type="date"
                            value={dateRange.startDate}
                            onChange={(e) => handleDateChange('startDate', e.target.value)}
                            className="w-full sm:w-auto border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                          />
                          <span className="mx-2">-</span>
                          <input
                            type="date"
                            value={dateRange.endDate}
                            onChange={(e) => handleDateChange('endDate', e.target.value)}
                            className="w-full sm:w-auto border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                          />
                        </div>
                      </div>
                      
                      {/* Filtro Utente */}
                      <div className="relative">
                        <button
                          onClick={() => setShowUserDropdown(!showUserDropdown)}
                          className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-48"
                        >
                          <User className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                          <span className="mr-2 truncate">{getSelectedUserName()}</span>
                          {showUserDropdown ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />}
                        </button>
                        
                        {showUserDropdown && (
                          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm max-h-60">
                            <div
                              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                                showGlobalStats ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
                              }`}
                              onClick={() => {
                                setShowGlobalStats(true);
                                setSelectedUserId(null);
                                setShowUserDropdown(false);
                              }}
                            >
                              <div className="flex items-center">
                                <Globe className="h-4 w-4 mr-2" />
                                <span className="block truncate">Alle Benutzer</span>
                              </div>
                              {showGlobalStats && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                  <Check className="h-4 w-4" />
                                </span>
                              )}
                            </div>
                            
                            {jkUsers.map((user) => (
                              <div
                                key={user.id}
                                className={`cursor-pointer select-none relative py-2 pl-6 pr-9 ${
                                  !showGlobalStats && selectedUserId === user.id ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
                                }`}
                                onClick={() => {
                                  setShowGlobalStats(false);
                                  setSelectedUserId(user.id);
                                  setShowUserDropdown(false);
                                }}
                              >
                                <div className="flex items-center">
                                  <User className="h-4 w-4 mr-2" />
                                  <span className="block">{user.username}</span>
                                </div>
                                {!showGlobalStats && selectedUserId === user.id && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                                    <Check className="h-4 w-4" />
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Pulsanti di controllo */}
                    <div className="flex gap-2">
                      <button
                        onClick={applyDateFilter}
                        className="flex items-center px-3 py-2 bg-indigo-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Anwenden
                      </button>
                      
                      <button
                        onClick={resetFilters}
                        className="flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        title="Filter zurücksetzen"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Banner filtro attivo */}
                  {(dateRange.startDate || dateRange.endDate || (!showGlobalStats && selectedUserId)) && (
                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Aktiver Filter:</span>
                        {dateRange.startDate && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            von {dateRange.startDate}
                          </span>
                        )}
                        {dateRange.endDate && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            bis {dateRange.endDate}
                          </span>
                        )}
                        {!showGlobalStats && selectedUserId && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Benutzer: {getSelectedUserName()}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

        {/* Statistiche Principali */}
        <div className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {/* Kunden geladen */}
            <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
              <div className="flex items-center">
                <Users className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-600">Kunden geladen</p>
                  <p className="text-xl sm:text-2xl font-bold text-blue-900">
                    {statistics?.total_customers_loaded?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Anrufversuche */}
            <div className="bg-indigo-50 p-3 sm:p-4 rounded-lg">
              <div className="flex items-center">
                <Phone className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-600 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-indigo-600">Anrufversuche</p>
                  <p className="text-xl sm:text-2xl font-bold text-indigo-900">
                    {statistics?.total_jk_calls?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Erreichte Kunden */}
            <div className="bg-green-50 p-3 sm:p-4 rounded-lg sm:col-span-2 lg:col-span-1">
              <div className="flex items-center">
                <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-600">Erreichte Kunden</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-900">
                    {statistics?.total_jk_closures?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
                  {/* Detaillierte Anrufstatistik */}
                  <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h3 className="text-lg font-medium text-gray-900">Detaillierte Anrufstatistik</h3>
                      <button
                        onClick={toggleSuspiciousCallsFilter}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          showSuspiciousCalls
                            ? 'bg-orange-100 text-orange-800 border border-orange-300'
                            : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                        }`}
                        title={showSuspiciousCalls ? 'Alle Anrufe anzeigen' : 'Verdächtige Anrufe anzeigen (< 15s mit positivem/negativem Feedback)'}
                      >
                        <AlertTriangle className="h-4 w-4" />
                        <span className="hidden sm:inline">
                          {showSuspiciousCalls ? 'Verdächtige Anrufe' : 'Qualitätskontrolle'}
                        </span>
                        {suspiciousCallsCount > 0 && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            showSuspiciousCalls ? 'bg-orange-200 text-orange-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {suspiciousCallsCount}
                          </span>
                        )}
                      </button>
                    </div>
            
            {statistics?.calls && statistics.calls.length > 0 ? (
              <div>
                {/* Tabella per desktop (nascosta su mobile) */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Datum
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Kunde
                        </th>
                                {showGlobalStats && (
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Benutzer
                                  </th>
                                )}
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Feedback
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Dauer
                                </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {currentCalls.map((call, index) => {
                        const isEven = index % 2 === 0;
                        const isSuspicious = !showSuspiciousCalls && isSuspiciousCall(call);
                        const rowBgClass = isSuspicious 
                          ? (isEven ? 'bg-orange-50' : 'bg-orange-100')
                          : (isEven ? 'bg-white' : 'bg-gray-100');
                        
                        return (
                          <React.Fragment key={index}>
                            <tr className={rowBgClass}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(call.call_date)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {call.customer_name}
                              </td>
                              {showGlobalStats && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {call.user_name}
                                </td>
                              )}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getFeedbackColor(call.new_feedback)}`}>
                                {getFeedbackLabel(call.new_feedback)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDuration(call.call_duration)}
                            </td>
                            </tr>
                            {call.notes && (
                              <tr className={rowBgClass}>
                                <td colSpan={showGlobalStats ? 5 : 4} className="px-6 pb-4 pt-0 text-sm text-gray-600">
                                  <span className="font-semibold">Notizen:</span> {call.notes}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Card per mobile (nascosta su desktop) */}
                <div className="md:hidden space-y-4">
                  {currentCalls.map((call, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="font-medium text-gray-900">{call.customer_name}</div>
                        <div className="text-sm text-gray-500">{formatDate(call.call_date)}</div>
                      </div>
                      
                      {showGlobalStats && (
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Benutzer:</span> {call.user_name}
                        </div>
                      )}
                      
                              {call.new_feedback && (
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-medium text-gray-500">Feedback:</span>
                                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getFeedbackColor(call.new_feedback)}`}>
                                    {getFeedbackLabel(call.new_feedback)}
                                  </span>
                                </div>
                              )}
                              
                              {call.call_duration && (
                                <div className="text-sm text-gray-600">
                                  <span className="font-medium">Dauer:</span> {formatDuration(call.call_duration)}
                                </div>
                              )}
                      
                      {call.notes && (
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Notizen:</span> {call.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Paginazione */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-500">
                      Zeige {indexOfFirstCall + 1} bis {Math.min(indexOfLastCall, filteredCalls.length)} von {filteredCalls.length} Anrufen
                      {showSuspiciousCalls && (
                        <span className="ml-2 text-orange-600 font-medium">
                          (Verdächtige Anrufe &lt; 15s)
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                        title="Zurück"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Zurück
                      </button>
                      
                      <span className="text-sm font-medium text-gray-900">
                        Seite {currentPage} von {totalPages}
                      </span>
                      
                      <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                        title="Weiter"
                      >
                        Weiter
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-48 sm:h-64 text-gray-500">
                <p className="text-lg">
                  {showSuspiciousCalls ? 'Keine verdächtigen Anrufe gefunden' : 'Keine Anrufdaten für den ausgewählten Zeitraum verfügbar'}
                </p>
                {showSuspiciousCalls && (
                  <p className="text-sm text-gray-400 mt-2">
                    Alle Anrufe haben eine angemessene Dauer oder neutrales Feedback
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default JKStatistics;
